import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Lock, Play, Coins, UserPlus } from 'lucide-react';
import { useIsCaptain, useIsAdmin } from '../../store/authStore';
import { useTour } from '../tour';
import { TOURS, TOUR_ORDER, type TourId } from '../tour';
import styles from './Onboarding.module.css';

// Missions that are always available (not locked behind previous missions)
const ALWAYS_AVAILABLE: TourId[] = ['refer_friend'];

interface OnboardingProgressWidgetProps {
  className?: string;
}

export const OnboardingProgressWidget: React.FC<OnboardingProgressWidgetProps> = ({
  className = '',
}) => {
  const navigate = useNavigate();
  const isCaptain = useIsCaptain();
  const isAdmin = useIsAdmin();
  const { startTour, completedTours, skippedTours, isTourActive } = useTour();

  // Only show for captains (not admins), hide once all missions completed
  if (!isCaptain || isAdmin) return null;
  if (completedTours.length >= TOUR_ORDER.length) return null;

  const completedCount = TOUR_ORDER.filter(id => completedTours.includes(id)).length;
  const earnedCredits = completedCount * 5;
  const totalCredits = TOUR_ORDER.length * 5;
  const progressPercent = (completedCount / TOUR_ORDER.length) * 100;

  const getTourStatus = (tourId: TourId, index: number): 'completed' | 'skipped' | 'available' | 'locked' => {
    if (completedTours.includes(tourId)) return 'completed';
    if (skippedTours.includes(tourId)) return 'skipped';
    if (ALWAYS_AVAILABLE.includes(tourId)) return 'available';
    if (index === 0) return 'available';
    // Unlock if previous tour is completed or skipped
    const prevTour = TOUR_ORDER[index - 1];
    if (completedTours.includes(prevTour) || skippedTours.includes(prevTour)) return 'available';
    return 'locked';
  };

  const handleAction = (tourId: TourId) => {
    if (tourId === 'refer_friend') {
      navigate('/settings');
    } else {
      startTour(tourId);
    }
  };

  const getActionLabel = (tourId: TourId, status: string) => {
    if (tourId === 'refer_friend') return 'Invite';
    if (status === 'skipped') return 'Retry';
    return 'Start';
  };

  const getIcon = (tourId: TourId, status: string) => {
    if (status === 'completed') return <CheckCircle2 size={20} />;
    if (status === 'locked') return <Lock size={16} />;
    if (tourId === 'refer_friend') return <UserPlus size={16} />;
    return <Play size={16} />;
  };

  return (
    <div className={`${styles.missionCard} ${className}`}>
      <div className={styles.missionCardHeader}>
        <span className={styles.missionCardTitle}>ONBOARDING MISSIONS</span>
        <span className={styles.missionCardCount}>{completedCount}/{TOUR_ORDER.length} done</span>
      </div>

      <div className={styles.missionList}>
        {TOUR_ORDER.map((tourId, index) => {
          const tour = TOURS[tourId];
          const status = getTourStatus(tourId, index);

          return (
            <div
              key={tourId}
              className={`${styles.missionRow} ${status === 'completed' ? styles.missionComplete : ''} ${status === 'locked' ? styles.missionLocked : ''}`}
            >
              <div className={styles.missionRowLeft}>
                <div className={styles.missionIcon}>
                  {getIcon(tourId, status)}
                </div>
                <div className={styles.missionInfo}>
                  <span className={styles.missionName}>{tour.title}</span>
                  <span className={styles.creditBadge}>
                    <Coins size={12} />
                    +{tour.credits} credits
                  </span>
                </div>
              </div>
              <div className={styles.missionAction}>
                {status === 'completed' ? (
                  <span className={styles.missionDoneLabel}>Completed</span>
                ) : status === 'skipped' || status === 'available' ? (
                  <button
                    className={styles.missionStartBtn}
                    onClick={() => handleAction(tourId)}
                    disabled={tourId !== 'refer_friend' && isTourActive}
                  >
                    {getActionLabel(tourId, status)}
                  </button>
                ) : (
                  <span className={styles.missionLockedLabel}>Locked</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.missionFooter}>
        <div className={styles.missionProgressLabel}>
          <span>Overall</span>
          <span>{earnedCredits}/{totalCredits} credits</span>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
        </div>
      </div>
    </div>
  );
};
